import { withStyles } from '@material-ui/core';

import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { GET_HHG_WEIGHT_IMPORT_TEMPLATES_FAILURE, GET_HHG_WEIGHT_IMPORT_TEMPLATE_FAILURE, POST_HHG_WEIGHT_IMPORT_TEMPLATE_DRYRUN_FAILURE, POST_HHG_WEIGHT_IMPORT_TEMPLATE_FAILURE, POST_HHG_WEIGHT_IMPORT_TEMPLATE_SUBMIT_FAILURE, PUT_HHG_WEIGHT_IMPORT_TEMPLATE_FAILURE } from './importHhgWeights.types';
import { Grid, Paper, Stack } from '@mui/material';
import { HHG_WEIGHT_IMPORT_TEMPLATE_TABS, HhgWeightTemplateEditor, HhgWeightTemplateEditorButton, HhgWeightTemplateSelector, SelectedHhgWeightImportTemplate, getHhgWeightImportResultErrors, getHhgWeightImportTemplateId, getHhgWeightImportTemplateList, hasHhgWeightImportRowErrors } from './importHhgWeights.components';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { getHhgWeightImportTemplate, getHhgWeightImportTemplates, postHhgWeightImportTemplate, putHhgWeightImportTemplate, submitHhgWeightImportTemplateFile, uploadHhgWeightImportTemplateFile } from './importHhgWeights.actions';
import { showToast } from 'modules/layout/layout.actions';

const styles = theme => ({
  root: {
    margin: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(1.5),
  },
});

function ImportHhgWeightsView(props) {
  const {
    classes,
    getHhgWeightImportTemplate: loadHhgWeightImportTemplate,
    getHhgWeightImportTemplates: loadHhgWeightImportTemplates,
    postHhgWeightImportTemplate: createHhgWeightImportTemplate,
    putHhgWeightImportTemplate: updateHhgWeightImportTemplate,
    showToast: showToastMessage,
    submitHhgWeightImportTemplateFile: submitHhgWeightImportTemplateFileForImport,
    uploadHhgWeightImportTemplateFile: dryRunHhgWeightImportTemplateFile,
  } = props;

  const [hhgWeightImportTemplates, setHhgWeightImportTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorTemplate, setEditorTemplate] = useState(null);
  const [selectedTemplateFile, setSelectedTemplateFile] = useState(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isDryRunLoading, setIsDryRunLoading] = useState(false);
  const [isImportSubmitting, setIsImportSubmitting] = useState(false);
  const [dryRunResults, setDryRunResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [selectedTemplateTab, setSelectedTemplateTab] = useState(HHG_WEIGHT_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);

  const fetchHhgWeightImportTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);

    const response = await loadHhgWeightImportTemplates();

    setIsLoadingTemplates(false);

    if (response.type === GET_HHG_WEIGHT_IMPORT_TEMPLATES_FAILURE) {
      showToastMessage('Failed to load HHG weights import templates. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setHhgWeightImportTemplates(getHhgWeightImportTemplateList(response.response));
  }, [loadHhgWeightImportTemplates, showToastMessage]);

  const handleTemplateSelected = useCallback(async templateId => {
    setSelectedTemplateId(templateId);
    setSelectedTemplate(null);
    setEditorTemplate(null);
    setSelectedTemplateFile(null);
    setDryRunResults(null);
    setImportResults(null);
    setSelectedTemplateTab(HHG_WEIGHT_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);

    if (!templateId) return;

    setIsLoadingTemplate(true);

    const response = await loadHhgWeightImportTemplate(templateId);

    setIsLoadingTemplate(false);

    if (response.type === GET_HHG_WEIGHT_IMPORT_TEMPLATE_FAILURE) {
      showToastMessage('Failed to load HHG weights import template. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setSelectedTemplate(response.response);
  }, [loadHhgWeightImportTemplate, showToastMessage]);

  const handleFileSelected = file => {
    setSelectedTemplateFile(file);
    setDryRunResults(null);
    setImportResults(null);
    setSelectedTemplateTab(HHG_WEIGHT_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);
  };

  const openTemplateEditor = () => {
    setEditorTemplate(selectedTemplate || null);
    setTemplateEditorOpen(true);
  };

  const handleTemplateSaved = async payload => {
    const templateId = getHhgWeightImportTemplateId(payload);
    const response = editorTemplate
      ? await updateHhgWeightImportTemplate(templateId, payload)
      : await createHhgWeightImportTemplate(payload);
    const failureType = editorTemplate ? PUT_HHG_WEIGHT_IMPORT_TEMPLATE_FAILURE : POST_HHG_WEIGHT_IMPORT_TEMPLATE_FAILURE;

    if (response.type === failureType) {
      showToastMessage('Failed to save HHG weights import template. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return false;
    }

    showToastMessage('HHG weights import template saved successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
    await fetchHhgWeightImportTemplates();

    const savedTemplateId = getHhgWeightImportTemplateId(response.response);
    if (savedTemplateId) {
      setSelectedTemplateId(String(savedTemplateId));
      setSelectedTemplate(response.response);
    }

    setEditorTemplate(null);
    setTemplateEditorOpen(false);
    return true;
  };

  const handleDryRun = async () => {
    const templateId = selectedTemplateId || getHhgWeightImportTemplateId(selectedTemplate);

    if (!templateId || !selectedTemplateFile || isDryRunLoading || isImportSubmitting) return;

    setIsDryRunLoading(true);
    setImportResults(null);

    const response = await dryRunHhgWeightImportTemplateFile(templateId, selectedTemplateFile);

    setIsDryRunLoading(false);

    if (response.type === POST_HHG_WEIGHT_IMPORT_TEMPLATE_DRYRUN_FAILURE) {
      setDryRunResults({ errors: response.messages ?? ['Failed to upload HHG weights import file. Please try again.'] });
      setSelectedTemplateTab(HHG_WEIGHT_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS);
      showToastMessage('HHG weights dry run failed. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setDryRunResults(response.response);
    setSelectedTemplateTab(HHG_WEIGHT_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS);

    if (getHhgWeightImportResultErrors(response.response).length > 0) {
      showToastMessage('HHG weights dry run completed with errors.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    showToastMessage('HHG weights dry run completed successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
  };

  const handleSubmitImport = async () => {
    const templateId = selectedTemplateId || getHhgWeightImportTemplateId(selectedTemplate);

    if (
      !templateId
      || !selectedTemplateFile
      || !dryRunResults
      || getHhgWeightImportResultErrors(dryRunResults).length > 0
      || isDryRunLoading
      || isImportSubmitting
    ) return;

    setIsImportSubmitting(true);

    const response = await submitHhgWeightImportTemplateFileForImport(templateId, selectedTemplateFile);

    setIsImportSubmitting(false);

    if (response.type === POST_HHG_WEIGHT_IMPORT_TEMPLATE_SUBMIT_FAILURE) {
      setImportResults({ errors: response.messages ?? ['Failed to submit HHG weights import file. Please try again.'] });
      setSelectedTemplateTab(HHG_WEIGHT_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS);
      showToastMessage('HHG weights submit failed. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setImportResults(response.response);
    setSelectedTemplateTab(HHG_WEIGHT_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS);

    if (getHhgWeightImportResultErrors(response.response).length > 0 || hasHhgWeightImportRowErrors(response.response)) {
      showToastMessage('HHG weights import completed with errors.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    showToastMessage('HHG weights import submitted successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
  };

  useEffect(() => {
    fetchHhgWeightImportTemplates();
  }, [fetchHhgWeightImportTemplates]);

  return (
    <div className={`${classes.root} p-2`}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <HhgWeightTemplateSelector
                isLoading={isLoadingTemplates}
                templates={hhgWeightImportTemplates}
                selectedTemplateId={selectedTemplateId}
                onTemplateSelected={handleTemplateSelected}
              />
              <HhgWeightTemplateEditorButton isEditMode={Boolean(selectedTemplate)} disabled={isLoadingTemplate} onClick={openTemplateEditor} />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          {(selectedTemplateId || isLoadingTemplate) && (
            <SelectedHhgWeightImportTemplate
              activeTab={selectedTemplateTab}
              dryRunResults={dryRunResults}
              importResults={importResults}
              isDryRunLoading={isDryRunLoading}
              isImportSubmitting={isImportSubmitting}
              isLoading={isLoadingTemplate}
              selectedTemplateFile={selectedTemplateFile}
              template={selectedTemplate}
              onDryRun={handleDryRun}
              onFileSelected={handleFileSelected}
              onSubmitImport={handleSubmitImport}
              onTabChange={setSelectedTemplateTab}
            />
          )}
        </Grid>
      </Grid>
      <HhgWeightTemplateEditor
        initialTemplate={editorTemplate}
        isEditMode={Boolean(editorTemplate)}
        open={templateEditorOpen}
        onClose={() => setTemplateEditorOpen(false)}
        onSaveTemplate={handleTemplateSaved}
      />
    </div>
  );
}

ImportHhgWeightsView.propTypes = {
  classes: PropTypes.object.isRequired,
  getHhgWeightImportTemplate: PropTypes.func.isRequired,
  getHhgWeightImportTemplates: PropTypes.func.isRequired,
  postHhgWeightImportTemplate: PropTypes.func.isRequired,
  putHhgWeightImportTemplate: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  submitHhgWeightImportTemplateFile: PropTypes.func.isRequired,
  uploadHhgWeightImportTemplateFile: PropTypes.func.isRequired,
};

export default compose(
  withStyles(styles),
  withRouter,
  connect(null, {
    getHhgWeightImportTemplate,
    getHhgWeightImportTemplates,
    postHhgWeightImportTemplate,
    putHhgWeightImportTemplate,
    showToast,
    submitHhgWeightImportTemplateFile,
    uploadHhgWeightImportTemplateFile,
  }),
)(ImportHhgWeightsView);
